// Single, unified input system.
//
// One binding map for everyone — no presets. Bindings use physical key codes
// (event.code, which is layout-independent), so a French AZERTY user pressing
// the key labeled "Z" generates `KeyW` exactly like a US-QWERTY user pressing
// the key labeled "W". The keymap overlay localizes labels at display time
// via navigator.keyboard.getLayoutMap, so each user reads their own
// keyboard's printed letters.
//
// Defaults are designed so a beginner can fly with ARROWS + SPACE alone:
//   ↑/↓     pitch up / down
//   ←/→     yaw left / right
//   Space   fire laser
//   X/C/V   skills (dash / missiles / shield)
// Optional advanced keys for power users:
//   W/S     throttle up / down
//   Q/E     roll left / right
//   A/D     yaw mirror (some prefer left hand)

export type Action =
  | "pitchUp" | "pitchDown" | "yawLeft" | "yawRight" | "rollLeft" | "rollRight"
  | "throttleUp" | "throttleDown"
  | "fire" | "skill1" | "skill2" | "skill3"
  | "boostToRing"
  | "toggleKeymap" | "openSettings" | "openChat" | "respawn";

export const ACTION_LABELS: Record<Action, string> = {
  pitchUp: "Pitch up (climb)",
  pitchDown: "Pitch down (dive)",
  yawLeft: "Yaw left",
  yawRight: "Yaw right",
  rollLeft: "Roll left",
  rollRight: "Roll right",
  throttleUp: "Throttle +",
  throttleDown: "Throttle -",
  fire: "Fire laser",
  skill1: "Skill — Dash",
  skill2: "Skill — Missiles",
  skill3: "Skill — Shield",
  boostToRing: "Boost to last kill ring",
  toggleKeymap: "Toggle key overlay",
  openSettings: "Open settings",
  openChat: "Open chat",
  respawn: "Respawn",
};

export type Bindings = Record<Action, string[]>;

export interface Settings {
  invertPitch: boolean;
  mouseSens: number;
  mouseAim: boolean;
  autoLevel: boolean;
  gyroAim: boolean;      // mobile: device tilt drives pitch + yaw
  // Audio
  masterVolume: number;
  sfxVolume: number;
  engineVolume: number;
  muted: boolean;
}

// v5 bindings: War-Thunder layout (W/S throttle, A/D roll, Q/E rudder, arrows
// for fallback pitch/yaw). v7 settings: invertPitch off (joystick convention
// baked into key choice), mouseAim on, autoLevel on.
const STORAGE_KEY  = "skybreak.bindings.v5";
const SETTINGS_KEY = "skybreak.settings.v7";

export function defaultBindings(): Bindings {
  // War Thunder layout (joystick convention encoded in the key choice itself):
  //   W/S    — throttle up/down
  //   A/D    — roll left/right
  //   Q/E    — rudder (yaw) left/right
  //   ↑      — pitch DOWN (push stick forward → nose dive)
  //   ↓      — pitch UP   (pull stick back  → nose climb)
  //   ← →    — yaw mirror
  //   Space  — fire laser
  //   X/C/V  — dash / missiles / shield
  //   Mouse  — primary aim when "Mouse Aim" is on (default)
  return {
    pitchUp:    ["ArrowDown"],                            // pull stick back
    pitchDown:  ["ArrowUp"],                              // push stick forward
    yawLeft:    ["KeyQ", "ArrowLeft"],                    // rudder
    yawRight:   ["KeyE", "ArrowRight"],
    rollLeft:   ["KeyA", "Comma"],
    rollRight:  ["KeyD", "Period"],
    throttleUp:   ["KeyW", "ShiftLeft", "ShiftRight"],
    throttleDown: ["KeyS", "ControlLeft", "ControlRight"],
    fire:       ["Space", "Mouse0"],
    skill1:     ["KeyX"],
    skill2:     ["KeyC"],
    skill3:     ["KeyV"],
    boostToRing: ["KeyB"],
    toggleKeymap: ["Tab", "F2"],
    openSettings: ["Escape"],
    openChat:     ["Enter"],
    respawn:      ["KeyR"],
  };
}

export function defaultSettings(): Settings {
  // invertPitch is FALSE because the WT-style key bindings already encode the
  // joystick convention (Up arrow = nose down, Down arrow = nose up).
  return {
    invertPitch: false, mouseSens: 1,
    mouseAim: true, autoLevel: true, gyroAim: false,
    masterVolume: 0.7, sfxVolume: 1, engineVolume: 0.6, muted: false,
  };
}

function emptyBindings(): Bindings {
  return {
    pitchUp: [], pitchDown: [], yawLeft: [], yawRight: [], rollLeft: [], rollRight: [],
    throttleUp: [], throttleDown: [], fire: [], skill1: [], skill2: [], skill3: [],
    boostToRing: [],
    toggleKeymap: [], openSettings: [], openChat: [], respawn: [],
  };
}

export class InputSystem {
  bindings: Bindings;
  settings: Settings;
  pressed = new Set<string>();
  private edgeQueue: Action[] = [];
  private throttle = 0.7;
  private lockedToUI = false;
  private suspended = false;
  private listeners = new Map<string, () => void>();
  // Mouse-aim cursor position in screen pixels.
  mouseX = 0; mouseY = 0;
  // Low-pass-filtered mouse-aim pitch/yaw. Removes hand/sensor jitter that
  // otherwise gets amplified through the response curve and feels twitchy.
  private smoothedMPitch = 0;
  private smoothedMYaw   = 0;

  constructor() {
    const raw = localStorage.getItem(STORAGE_KEY);
    this.bindings = raw ? JSON.parse(raw) : defaultBindings();
    const s = localStorage.getItem(SETTINGS_KEY);
    this.settings = s ? { ...defaultSettings(), ...JSON.parse(s) } : defaultSettings();
    this.mouseX = window.innerWidth / 2;
    this.mouseY = window.innerHeight / 2;
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("mousedown", this.onMouseDown);
    window.addEventListener("mouseup", this.onMouseUp);
    window.addEventListener("mousemove", this.onMouseMove);
    window.addEventListener("blur", () => this.pressed.clear());
  }

  setSuspended(v: boolean) { this.suspended = v; if (v) this.pressed.clear(); }
  setLockedToUI(v: boolean) { this.lockedToUI = v; }
  getThrottle(): number { return this.throttle; }
  setThrottle(v: number): void { this.throttle = Math.max(0, Math.min(1, v)); }
  // Synthetic skill press from a touch UI / external source.
  queueSkill(n: 1 | 2 | 3): void {
    this.edgeQueue.push(n === 1 ? "skill1" : n === 2 ? "skill2" : "skill3");
  }

  saveBindings() { localStorage.setItem(STORAGE_KEY, JSON.stringify(this.bindings)); }
  saveSettings() { localStorage.setItem(SETTINGS_KEY, JSON.stringify(this.settings)); }

  resetBindings() {
    this.bindings = defaultBindings();
    this.saveBindings();
  }

  rebind(action: Action, key: string) {
    for (const k of Object.keys(this.bindings) as Action[]) {
      this.bindings[k] = this.bindings[k].filter(x => x !== key);
    }
    this.bindings[action].push(key);
    if (this.bindings[action].length > 4) this.bindings[action].shift();
    this.saveBindings();
  }

  unbind(action: Action, key: string) {
    this.bindings[action] = this.bindings[action].filter(k => k !== key);
    this.saveBindings();
  }

  on(action: Action, fn: () => void) {
    this.listeners.set(action, fn);
  }

  private onKeyDown = (e: KeyboardEvent) => {
    if (this.suspended) return;
    const key = e.code;
    if (this.pressed.has(key)) return;
    this.pressed.add(key);
    this.handleEdge(key, e);
  };
  private onKeyUp = (e: KeyboardEvent) => { this.pressed.delete(e.code); };
  private onMouseDown = (e: MouseEvent) => {
    if (this.suspended) return;
    const key = "Mouse" + e.button;
    if (this.pressed.has(key)) return;
    this.pressed.add(key);
    this.handleEdge(key, e);
  };
  private onMouseUp = (e: MouseEvent) => { this.pressed.delete("Mouse" + e.button); };
  private onMouseMove = (e: MouseEvent) => { this.mouseX = e.clientX; this.mouseY = e.clientY; };

  private handleEdge(key: string, e: { preventDefault: () => void }) {
    for (const action of ["openChat", "openSettings", "toggleKeymap", "respawn", "boostToRing"] as Action[]) {
      if (this.bindings[action].includes(key)) {
        const cb = this.listeners.get(action);
        if (cb) { cb(); e.preventDefault(); return; }
      }
    }
    if (this.lockedToUI) return;
    for (const action of ["skill1", "skill2", "skill3"] as Action[]) {
      if (this.bindings[action].includes(key)) {
        this.edgeQueue.push(action);
        e.preventDefault();
      }
    }
    if (this.bindings.fire.includes(key)) e.preventDefault();
  }

  private isDown(action: Action): boolean {
    if (this.lockedToUI) return false;
    const keys = this.bindings[action];
    for (const k of keys) if (this.pressed.has(k)) return true;
    return false;
  }

  sample(dt: number, seq: number): InputStatePayload {
    // ===== Pitch / yaw =====
    // Keyboard input first (server pitch +1 = nose up).
    let pitch = (this.isDown("pitchUp") ? 1 : 0) - (this.isDown("pitchDown") ? 1 : 0);
    if (this.settings.invertPitch) pitch = -pitch;
    let yaw  = (this.isDown("yawRight") ? 1 : 0) - (this.isDown("yawLeft") ? 1 : 0);

    // Mouse-aim overlay (War-Thunder style). Keyboard takes priority for fine
    // control; mouse fills in otherwise. We:
    //   1. Map cursor offset → input via DEADZONE + LINEAR response (with a
    //      gentle cubic ease so center is delicate without amplifying noise).
    //   2. Apply a low-pass filter so hand jitter / mouse polling noise
    //      doesn't make the plane wander ("random movement" symptom).
    if (this.settings.mouseAim && !this.lockedToUI && !this.suspended) {
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      const half = Math.min(cx, cy);
      const dx = (this.mouseX - cx) / half;
      const dy = (this.mouseY - cy) / half;
      const sens = this.settings.mouseSens;
      const dead = 0.08;
      const apply = (v: number) => {
        const a = Math.abs(v);
        if (a < dead) return 0;
        const sign = v < 0 ? -1 : 1;
        const t = Math.min(1, (a - dead) / (1 - dead));
        // 85% linear + 15% quadratic. Predictable response, slightly more
        // bite at the edge for tight turns.
        const eased = 0.85 * t + 0.15 * t * t;
        return sign * eased * sens;
      };
      const mPitch = -apply(dy);
      const mYaw   =  apply(dx);
      // Low-pass smoothing (alpha=0.35 → ~60 ms time-constant at 30 Hz).
      const alpha = Math.min(1, dt * 12);
      this.smoothedMPitch = this.smoothedMPitch * (1 - alpha) + mPitch * alpha;
      this.smoothedMYaw   = this.smoothedMYaw   * (1 - alpha) + mYaw   * alpha;
      if (pitch === 0) pitch = this.smoothedMPitch;
      if (yaw === 0)   yaw   = this.smoothedMYaw;
    }

    const roll = (this.isDown("rollRight") ? 1 : 0) - (this.isDown("rollLeft") ? 1 : 0);

    if (this.isDown("throttleUp"))   this.throttle = Math.min(1, this.throttle + dt * 1.0);
    if (this.isDown("throttleDown")) this.throttle = Math.max(0, this.throttle - dt * 1.0);

    let skill: 0 | 1 | 2 | 3 = 0;
    while (this.edgeQueue.length) {
      const a = this.edgeQueue.shift()!;
      if (a === "skill1") { skill = 1; break; }
      if (a === "skill2") { skill = 2; break; }
      if (a === "skill3") { skill = 3; break; }
    }
    return {
      pitch, yaw, roll,
      throttle: this.throttle,
      fire: this.isDown("fire"),
      skill,
      seq,
      autoLevel: this.settings.autoLevel,
    };
  }
}

export interface InputStatePayload {
  pitch: number; yaw: number; roll: number;
  throttle: number;
  fire: boolean;
  skill: 0 | 1 | 2 | 3;
  seq: number;
  autoLevel: boolean;
  // Optional cursor-aim direction (filled by main.ts after sample()).
  aimX?: number; aimY?: number; aimZ?: number;
}

// Cached map of physical key code → printed character on the user's keyboard.
let layoutMap: Map<string, string> | null = null;

export async function primeLayoutLabels(): Promise<void> {
  const nav = navigator as Navigator & { keyboard?: { getLayoutMap?: () => Promise<Map<string, string>> } };
  if (!nav.keyboard?.getLayoutMap) return;
  try {
    layoutMap = await nav.keyboard.getLayoutMap();
  } catch { /* ignore */ }
}

export function keyLabel(code: string): string {
  if (layoutMap && code.startsWith("Key")) {
    const ch = layoutMap.get(code);
    if (ch) return ch.toUpperCase();
  }
  if (code.startsWith("Key")) return code.slice(3);
  if (code.startsWith("Digit")) return code.slice(5);
  if (code.startsWith("Arrow")) return { ArrowUp: "↑", ArrowDown: "↓", ArrowLeft: "←", ArrowRight: "→" }[code as "ArrowUp"]!;
  if (code === "Space") return "Space";
  if (code === "ShiftLeft") return "L Shift";
  if (code === "ShiftRight") return "R Shift";
  if (code === "ControlLeft") return "L Ctrl";
  if (code === "ControlRight") return "R Ctrl";
  if (code === "Comma") return ",";
  if (code === "Period") return ".";
  if (code === "Escape") return "Esc";
  if (code === "Enter") return "Enter";
  if (code === "Tab") return "Tab";
  if (code.startsWith("F") && /^F\d+$/.test(code)) return code;
  if (code === "Mouse0") return "L Click";
  if (code === "Mouse1") return "M Click";
  if (code === "Mouse2") return "R Click";
  return code;
}
